// Color tokens. Each `colors.X` returns `var(--color-X)`, so the actual hex
// value comes from CSS variables defined in `index.css`. Toggling
// `document.documentElement.dataset.theme = 'light'` repaints the UI without
// any React re-renders.
type ColorName =
  | 'background'
  | 'surface'
  | 'primary'
  | 'primaryDark'
  | 'text'
  | 'textSecondary'
  | 'border'
  | 'warning'
  | 'danger'
  | 'success'
  | 'card'
  | 'white'
  | 'secondary';

const COLOR_NAMES: ColorName[] = [
  'background',
  'surface',
  'primary',
  'primaryDark',
  'text',
  'textSecondary',
  'border',
  'warning',
  'danger',
  'success',
  'card',
  'white',
  'secondary',
];

const colors = COLOR_NAMES.reduce(
  (acc, name) => {
    acc[name] = `var(--color-${name})`;
    return acc;
  },
  {} as Record<ColorName, string>
);

// Common 2-digit hex alpha values mapped to decimal opacity. Anything else
// falls back to parseInt-based conversion so calls like `alpha('primary', 'FF')`
// keep working.
const HEX_ALPHA: Record<string, number> = {
  '00': 0,
  '05': 0.02,
  '08': 0.03,
  '09': 0.035,
  '0D': 0.05,
  '10': 0.06,
  '11': 0.07,
  '14': 0.08,
  '15': 0.08,
  '18': 0.09,
  '1A': 0.1,
  '20': 0.13,
  '22': 0.13,
  '30': 0.19,
  '33': 0.2,
  '40': 0.25,
  '4D': 0.3,
  '50': 0.31,
  '60': 0.38,
  '66': 0.4,
  '70': 0.44,
  '80': 0.5,
  '90': 0.56,
  '99': 0.6,
  'AA': 0.67,
  'B3': 0.7,
  'CC': 0.8,
  'E6': 0.9,
  'EE': 0.93,
  'FF': 1,
};

/**
 * Returns a CSS color string with the requested alpha applied to one of the
 * theme tokens. Accepts the 2-digit hex alpha used historically (e.g. '30')
 * or a number 0–1.
 */
export function alpha(name: ColorName, opacity: string | number): string {
  let value: number;
  if (typeof opacity === 'number') {
    value = Math.max(0, Math.min(1, opacity));
  } else {
    const upper = opacity.toUpperCase();
    value =
      HEX_ALPHA[upper] !== undefined
        ? HEX_ALPHA[upper]
        : Math.max(0, Math.min(1, parseInt(upper, 16) / 255));
  }
  return `rgb(var(--color-${name}-rgb) / ${value})`;
}

const shadow = '0 2px 8px rgba(0,0,0,0.25)';

export const theme = {
  colors: { ...colors, shadow },
  fonts: {
    heading: "'Plus Jakarta Sans', system-ui, sans-serif",
    body: "'Plus Jakarta Sans', system-ui, sans-serif",
    weightBold: 700,
    weightSemibold: 600,
    weightNormal: 400,
    sizeTitle: '2rem',
    sizeSubtitle: '1.25rem',
    sizeBody: '1rem',
    sizeSmall: '0.875rem',
    letterSpacingHeading: '-0.02em',
    letterSpacingWide: '0.01em',
  },
  borderRadius: {
    card: '18px',
    button: '12px',
    input: '10px',
  },
  spacing: {
    xs: '6px',
    sm: '12px',
    md: '20px',
    lg: '32px',
  },
};
